import { useEffect } from 'react';

/**
 * Restores focus to an inline editor after the leave-confirmation dialog closes via "Stay"
 * (DL-60097).
 *
 * Editors stay mounted across the leave prompt — either because they're popper-based
 * (single- / multi-select) with a controlled `open={isEditing}` prop, or because
 * `isEditing` itself doesn't change during the Stay flow. Either way `useFocusOnEdit`
 * (keyed on `isEditing`) doesn't re-fire, and opening the "Leave without saving?" dialog
 * steals focus to whatever opened it (the nav link), not back to our input.
 *
 * Runs on every render while editing (no dep array) but is a no-op unless focus has
 * actually drifted outside the editor, and never steals focus while a dialog is on
 * screen — so it doesn't fight MUI Dialog's FocusTrap during the prompt itself or on
 * background re-renders (e.g. query refetch) that land while the user is reading it.
 *
 * @param ref - Ref to either the editor container (popper cells) or directly to the
 *   focusable control (text / number cells where the ref IS the `<input>`).
 * @param isEditing - Whether the cell is in edit mode.
 * @param selector - Optional CSS selector for a child to focus inside the container.
 *   When omitted, the ref's element is focused directly.
 */
export function useRestoreFocusOnStay(
  ref: React.RefObject<HTMLElement | null>,
  isEditing: boolean,
  selector?: string
): void {
  useEffect(() => {
    if (!isEditing) return;
    const container = ref.current;
    if (!container) return;
    const active = document.activeElement;
    // A dialog is on screen (the leave prompt, or any other) — leave its FocusTrap alone.
    if (active instanceof Element && active.closest('[role="dialog"]')) return;
    const target = selector ? container.querySelector<HTMLElement>(selector) : container;
    if (!target) return;
    if (active === target) return;
    // For popper editors (selector given) also skip when focus is anywhere inside the
    // container — the input is one of many focusable children (options, clear button).
    if (selector && active instanceof Node && container.contains(active)) return;
    target.focus();
  });
}
